import type { ReactNode } from 'react';
import { LogoAvatar, RemoteUserAvatar } from '../../lib/avatars.js';
import type { AvatarPresetId } from '../../lib/types/membership.js';
import { operatorOrgLogoUrl, operatorUserAvatarUrl } from '../../lib/operatorApi.js';
import { BRAND_COLOR_DEFS } from '../profile/firstLoginProfileConstants.js';

/** An organization's logo as its avatar in operator tables and headers. */
export function OrgAvatar({
  slug,
  name,
  size = 32,
}: {
  readonly slug: string;
  readonly name: string;
  readonly size?: number;
}) {
  return <LogoAvatar name={name || slug} src={operatorOrgLogoUrl(slug)} size={size} />;
}

/**
 * A member's own avatar (uploaded photo, else the preset they chose, else the
 * first letter) wherever their name appears in an operator table.
 * `hasPhoto` skips the photo request when the list already says there is none.
 */
export function OrgUserAvatar({
  slug,
  accountId,
  name,
  preset,
  hasPhoto,
  size = 28,
}: {
  readonly slug: string;
  readonly accountId: string | null | undefined;
  readonly name: string;
  readonly preset?: string | null;
  readonly hasPhoto?: boolean;
  readonly size?: number;
}) {
  const photoUrl = accountId && hasPhoto !== false ? operatorUserAvatarUrl(slug, accountId) : null;
  return (
    <RemoteUserAvatar
      name={name}
      photoUrl={photoUrl}
      preset={(preset ?? null) as AvatarPresetId | null}
      size={size}
    />
  );
}

/** Avatar + label on one line, the layout every table cell below uses. */
export function WithAvatar({
  avatar,
  children,
}: {
  readonly avatar: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      {avatar}
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

/** The accent color a person chose in their profile: a dot in that color and its name. */
export function BrandColorChip({ color }: { readonly color: string | null | undefined }) {
  const def = BRAND_COLOR_DEFS.find((c) => c.id === color);
  if (!def) return <span style={{ color: 'var(--sk-fg-muted)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        aria-hidden
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: def.color,
          boxShadow: '0 0 0 1px var(--sk-border)',
        }}
      />
      {def.label}
    </span>
  );
}
